import React, { useEffect, useState } from "react";
import { getOfflineQueue, removeQueued, clearOfflineQueue } from "@/lib/offline";
import { syncOfflineQueue, retryQueuedItem, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { CloudOff, RefreshCw, Trash2, CheckCircle2, AlertTriangle, Clock } from "lucide-react";

const OP_LABELS = [
  [/\/invoices/, "فاتورة"],
  [/\/expenses/, "مصروف"],
  [/\/parties/, "جهة (عميل/مورد)"],
  [/\/products/, "منتج"],
  [/\/payments|\/receipts/, "دفعة/سند"],
];
const opLabel = (url = "") => (OP_LABELS.find(([re]) => re.test(url)) || [null, "عملية"])[1];

// Errors stored by older builds may be an array/object (raw 422 detail). Coerce to
// a safe string so rendering never throws "Objects are not valid as a React child".
const errText = (v) => {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (Array.isArray(v)) return v.map((x) => x?.msg || (typeof x === "string" ? x : JSON.stringify(x))).join(" • ");
  if (typeof v === "object") return v.msg || JSON.stringify(v);
  return String(v);
};

function parseData(raw) {
  try { return typeof raw === "string" ? JSON.parse(raw) : (raw || {}); } catch { return {}; }
}
function describe(item) {
  const d = parseData(item.data);
  const parts = [];
  if (d.kind) parts.push({ sale: "بيع", purchase: "شراء", sale_return: "مرتجع بيع", purchase_return: "مرتجع شراء" }[d.kind] || d.kind);
  if (d.party_name) parts.push(d.party_name);
  if (d.total != null) parts.push(`${Number(d.total).toLocaleString("ar-EG")} ${d.currency || ""}`.trim());
  if (d.amount != null) parts.push(`${Number(d.amount).toLocaleString("ar-EG")} ${d.currency || ""}`.trim());
  if (d.name && !d.party_name) parts.push(d.name);
  return parts.join(" · ");
}

export default function SyncQueue() {
  const [queue, setQueue] = useState(() => getOfflineQueue());
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState([]);
  const refresh = () => {
    const q = getOfflineQueue();
    setQueue(q);
    setSelected((s) => s.filter((id) => q.some((x) => x.id === id)));
  };

  const allSelected = queue.length > 0 && selected.length === queue.length;
  const toggleOne = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setSelected(() => (queue.length > 0 && selected.length === queue.length ? [] : queue.map((q) => q.id)));

  const syncSelected = async () => {
    if (!navigator.onLine) { toast.error("لا يوجد اتصال بالإنترنت حالياً"); return; }
    const items = queue.filter((q) => selected.includes(q.id));
    if (!items.length) { toast.info("لم تحدّد أي عملية"); return; }
    setBusy(true);
    let ok = 0, fail = 0;
    for (const item of items) {
      if (!navigator.onLine) break;
      try { await retryQueuedItem(item); ok += 1; }
      catch (e) { fail += 1; }
    }
    refresh();
    setBusy(false);
    if (ok) toast.success(`تمت مزامنة ${ok} عملية وانعكست على الصندوق`);
    if (fail) toast.warning(`تعذّرت مزامنة ${fail} عملية — راجع سبب الفشل`);
  };

  useEffect(() => {
    window.addEventListener("offline-queue-changed", refresh);
    return () => window.removeEventListener("offline-queue-changed", refresh);
  }, []);

  const syncAll = async () => {
    if (!navigator.onLine) { toast.error("لا يوجد اتصال بالإنترنت حالياً"); return; }
    setBusy(true);
    try {
      const r = await syncOfflineQueue();
      refresh();
      if (r.flushed) toast.success(`تمت مزامنة ${r.flushed} عملية`);
      if (r.remaining) toast.warning(`تبقّى ${r.remaining} عملية — راجع سبب الفشل`);
      if (!r.flushed && !r.remaining) toast.info("لا توجد عمليات معلّقة");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const retryOne = async (item) => {
    if (!navigator.onLine) { toast.error("لا يوجد اتصال بالإنترنت حالياً"); return; }
    setBusy(true);
    try { await retryQueuedItem(item); refresh(); toast.success("تمت المزامنة"); }
    catch (e) { refresh(); toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const removeOne = (id) => { if (!confirm("حذف هذه العملية من الطابور نهائياً؟")) return; removeQueued(id); refresh(); toast.success("تم الحذف"); };
  const clearAll = () => { if (!confirm("مسح كل العمليات المعلّقة؟ لن تتم مزامنتها.")) return; clearOfflineQueue(); refresh(); };

  const online = navigator.onLine;

  return (
    <div className="space-y-6 max-w-4xl" dir="rtl" data-testid="sync-queue-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold flex items-center gap-2">
            <CloudOff className="w-7 h-7 text-amber-500" /> العمليات المعلّقة
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            العمليات التي حُفظت محلياً أثناء انقطاع الإنترنت وتنتظر المزامنة مع الخادم.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={syncSelected} disabled={busy || selected.length === 0} variant="secondary" className="gap-2" data-testid="sync-selected-btn">
            <CheckCircle2 className="w-4 h-4" /> حفظ المحدد {selected.length ? `(${selected.length})` : ""}
          </Button>
          <Button onClick={syncAll} disabled={busy || queue.length === 0} className="gap-2" data-testid="sync-all-btn">
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> مزامنة الكل
          </Button>
          {queue.length > 0 && (
            <Button variant="outline" onClick={clearAll} className="gap-2 text-destructive" data-testid="clear-queue-btn">
              <Trash2 className="w-4 h-4" /> مسح الكل
            </Button>
          )}
        </div>
      </div>

      <div className={`flex items-center gap-2 text-sm rounded-lg px-3 py-2 ${online ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="sync-connection-state">
        {online ? <CheckCircle2 className="w-4 h-4" /> : <CloudOff className="w-4 h-4" />}
        {online ? "متصل بالإنترنت — تتم المزامنة تلقائياً" : "غير متصل — ستتم المزامنة تلقائياً عند عودة الاتصال"}
      </div>

      {queue.length > 0 && (
        <div className="flex items-center gap-2 px-1" data-testid="select-all-row">
          <Checkbox id="sel-all" checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all-checkbox" />
          <label htmlFor="sel-all" className="text-sm font-medium cursor-pointer select-none">
            تحديد الكل ({selected.length}/{queue.length})
          </label>
        </div>
      )}

      {queue.length === 0 ? (
        <Card className="p-10 text-center" data-testid="sync-empty">
          <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500" />
          <div className="mt-3 font-heading font-bold text-lg">لا توجد عمليات معلّقة</div>
          <div className="text-sm text-muted-foreground mt-1">كل شيء متزامن مع الخادم ✓</div>
        </Card>
      ) : (
        <div className="space-y-3">
          {queue.map((item) => (
            <Card key={item.id} className="p-4" data-testid={`queue-item-${item.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-3 min-w-0">
                  <Checkbox className="mt-1 shrink-0" checked={selected.includes(item.id)} onCheckedChange={() => toggleOne(item.id)} data-testid={`queue-check-${item.id}`} />
                  <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold px-2 py-0.5 rounded bg-primary/10 text-primary">{opLabel(item.url)}</span>
                    <span className="text-[10px] font-mono text-muted-foreground">{item.method}</span>
                    {describe(item) && <span className="font-semibold">{describe(item)}</span>}
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground flex-wrap">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(item.created_at).toLocaleString("ar-EG")}</span>
                    {item.attempts > 0 && <span>محاولات: {item.attempts}</span>}
                  </div>
                  {item.last_error && (
                    <div className="flex items-center gap-1.5 mt-2 text-xs text-destructive bg-destructive/5 rounded px-2 py-1">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> {errText(item.last_error)}
                    </div>
                  )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={() => retryOne(item)} disabled={busy} data-testid={`retry-${item.id}`}>
                    <RefreshCw className="w-3.5 h-3.5" /> إعادة محاولة
                  </Button>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => removeOne(item.id)} data-testid={`remove-${item.id}`} title="حذف">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
